import { create } from 'zustand';
import type { DungeonMapData, DungeonRoom, DungeonCorridor, DungeonEntity, DungeonTrigger, DungeonQuest } from '@/lib/db/dungeon-types';
import { createEmptyMapData } from '@/lib/db/dungeon-types';

export type ToolType = 'select' | 'hand' | 'room' | 'corridor' | 'door' | 'entity' | 'trigger' | 'erase';

interface DungeonMeta {
  id: string;
  name: string;
  description: string;
  theme: string;
  status: 'draft' | 'published';
}

interface DungeonEditorState {
  meta: DungeonMeta | null;
  mapData: DungeonMapData;
  selectedTool: ToolType;
  selectedRoomId: string | null;
  selectedEntityId: string | null;
  selectedTriggerId: string | null;
  selectedQuestId: string | null;
  zoom: number;
  panOffset: { x: number; y: number };
  isDirty: boolean;
  undoStack: DungeonMapData[];
  redoStack: DungeonMapData[];
  isPlaytesting: boolean;

  // Meta actions
  setDungeon: (meta: DungeonMeta, mapData: DungeonMapData) => void;
  updateMeta: (updates: Partial<DungeonMeta>) => void;
  reset: () => void;

  // Tool & selection actions
  setTool: (tool: ToolType) => void;
  selectRoom: (id: string | null) => void;
  selectEntity: (id: string | null) => void;
  selectTrigger: (id: string | null) => void;
  selectQuest: (id: string | null) => void;
  clearSelection: () => void;

  // Viewport actions
  setZoom: (zoom: number) => void;
  setPanOffset: (offset: { x: number; y: number }) => void;

  // Map editing actions (all push undo)
  addRoom: (room: DungeonRoom) => void;
  updateRoom: (id: string, updates: Partial<DungeonRoom>) => void;
  removeRoom: (id: string) => void;
  addCorridor: (corridor: DungeonCorridor) => void;
  updateCorridor: (id: string, updates: Partial<DungeonCorridor>) => void;
  removeCorridor: (id: string) => void;
  addEntity: (entity: DungeonEntity) => void;
  updateEntity: (id: string, updates: Partial<DungeonEntity>) => void;
  removeEntity: (id: string) => void;
  addTrigger: (trigger: DungeonTrigger) => void;
  updateTrigger: (id: string, updates: Partial<DungeonTrigger>) => void;
  removeTrigger: (id: string) => void;
  addQuest: (quest: DungeonQuest) => void;
  updateQuest: (id: string, updates: Partial<DungeonQuest>) => void;
  removeQuest: (id: string) => void;

  // Undo/redo
  undo: () => void;
  redo: () => void;
  pushUndo: () => void;
  setMapData: (mapData: DungeonMapData) => void;

  // Other
  setPlaytesting: (active: boolean) => void;
  markClean: () => void;
}

const MAX_UNDO = 50;

export const useDungeonEditorStore = create<DungeonEditorState>((set, get) => ({
  meta: null,
  mapData: createEmptyMapData(60, 45),
  selectedTool: 'select',
  selectedRoomId: null,
  selectedEntityId: null,
  selectedTriggerId: null,
  selectedQuestId: null,
  zoom: 1,
  panOffset: { x: 0, y: 0 },
  isDirty: false,
  undoStack: [],
  redoStack: [],
  isPlaytesting: false,

  setDungeon: (meta, mapData) => set({ meta, mapData, isDirty: false, undoStack: [], redoStack: [] }),
  updateMeta: (updates) => set((s) => ({ meta: s.meta ? { ...s.meta, ...updates } : null, isDirty: true })),
  reset: () => set({
    meta: null, mapData: createEmptyMapData(60, 45), selectedTool: 'select',
    selectedRoomId: null, selectedEntityId: null, selectedTriggerId: null, selectedQuestId: null,
    zoom: 1, panOffset: { x: 0, y: 0 }, isDirty: false, undoStack: [], redoStack: [], isPlaytesting: false,
  }),

  setTool: (tool) => set({ selectedTool: tool }),
  selectRoom: (id) => set({ selectedRoomId: id, selectedEntityId: null, selectedTriggerId: null, selectedQuestId: null }),
  selectEntity: (id) => set({ selectedEntityId: id, selectedRoomId: null, selectedTriggerId: null, selectedQuestId: null }),
  selectTrigger: (id) => set({ selectedTriggerId: id, selectedRoomId: null, selectedEntityId: null, selectedQuestId: null }),
  selectQuest: (id) => set({ selectedQuestId: id, selectedRoomId: null, selectedEntityId: null, selectedTriggerId: null }),
  clearSelection: () => set({ selectedRoomId: null, selectedEntityId: null, selectedTriggerId: null, selectedQuestId: null }),

  setZoom: (zoom) => set({ zoom: Math.max(0.3, Math.min(4, zoom)) }),
  setPanOffset: (offset) => set({ panOffset: offset }),

  pushUndo: () => set((s) => ({
    undoStack: [...s.undoStack.slice(-MAX_UNDO + 1), structuredClone(s.mapData)],
    redoStack: [],
  })),
  setMapData: (mapData) => set({ mapData, isDirty: true }),

  addRoom: (room) => { get().pushUndo(); set((s) => ({ mapData: { ...s.mapData, rooms: [...s.mapData.rooms, room] }, isDirty: true, selectedRoomId: room.id })); },
  updateRoom: (id, updates) => { get().pushUndo(); set((s) => ({ mapData: { ...s.mapData, rooms: s.mapData.rooms.map((r) => (r.id === id ? { ...r, ...updates } : r)) }, isDirty: true })); },
  removeRoom: (id) => { get().pushUndo(); set((s) => ({
    mapData: { ...s.mapData, rooms: s.mapData.rooms.filter((r) => r.id !== id), corridors: s.mapData.corridors.filter((c) => c.fromRoomId !== id && c.toRoomId !== id), entities: s.mapData.entities.filter((e) => e.roomId !== id), triggers: s.mapData.triggers.filter((t) => t.roomId !== id) },
    isDirty: true, selectedRoomId: null,
  })); },

  addCorridor: (corridor) => { get().pushUndo(); set((s) => ({ mapData: { ...s.mapData, corridors: [...s.mapData.corridors, corridor] }, isDirty: true })); },
  updateCorridor: (id, updates) => { get().pushUndo(); set((s) => ({ mapData: { ...s.mapData, corridors: s.mapData.corridors.map((c) => (c.id === id ? { ...c, ...updates } : c)) }, isDirty: true })); },
  removeCorridor: (id) => { get().pushUndo(); set((s) => ({ mapData: { ...s.mapData, corridors: s.mapData.corridors.filter((c) => c.id !== id) }, isDirty: true })); },

  addEntity: (entity) => { get().pushUndo(); set((s) => ({ mapData: { ...s.mapData, entities: [...s.mapData.entities, entity] }, isDirty: true, selectedEntityId: entity.id })); },
  updateEntity: (id, updates) => { get().pushUndo(); set((s) => ({ mapData: { ...s.mapData, entities: s.mapData.entities.map((e) => (e.id === id ? { ...e, ...updates } : e)) }, isDirty: true })); },
  removeEntity: (id) => { get().pushUndo(); set((s) => ({ mapData: { ...s.mapData, entities: s.mapData.entities.filter((e) => e.id !== id), triggers: s.mapData.triggers.filter((t) => t.targetEntityId !== id) }, isDirty: true, selectedEntityId: null })); },

  addTrigger: (trigger) => { get().pushUndo(); set((s) => ({ mapData: { ...s.mapData, triggers: [...s.mapData.triggers, trigger] }, isDirty: true, selectedTriggerId: trigger.id })); },
  updateTrigger: (id, updates) => { get().pushUndo(); set((s) => ({ mapData: { ...s.mapData, triggers: s.mapData.triggers.map((t) => (t.id === id ? { ...t, ...updates } : t)) }, isDirty: true })); },
  removeTrigger: (id) => { get().pushUndo(); set((s) => ({ mapData: { ...s.mapData, triggers: s.mapData.triggers.filter((t) => t.id !== id) }, isDirty: true, selectedTriggerId: null })); },

  addQuest: (quest) => { get().pushUndo(); set((s) => ({ mapData: { ...s.mapData, quests: [...s.mapData.quests, quest] }, isDirty: true, selectedQuestId: quest.id })); },
  updateQuest: (id, updates) => { get().pushUndo(); set((s) => ({ mapData: { ...s.mapData, quests: s.mapData.quests.map((q) => (q.id === id ? { ...q, ...updates } : q)) }, isDirty: true })); },
  removeQuest: (id) => { get().pushUndo(); set((s) => ({ mapData: { ...s.mapData, quests: s.mapData.quests.filter((q) => q.id !== id) }, isDirty: true, selectedQuestId: null })); },

  undo: () => set((s) => {
    if (s.undoStack.length === 0) return s;
    const prev = s.undoStack[s.undoStack.length - 1];
    return { undoStack: s.undoStack.slice(0, -1), redoStack: [...s.redoStack, structuredClone(s.mapData)], mapData: prev, isDirty: true };
  }),
  redo: () => set((s) => {
    if (s.redoStack.length === 0) return s;
    const next = s.redoStack[s.redoStack.length - 1];
    return { redoStack: s.redoStack.slice(0, -1), undoStack: [...s.undoStack, structuredClone(s.mapData)], mapData: next, isDirty: true };
  }),

  setPlaytesting: (active) => set({ isPlaytesting: active }),
  markClean: () => set({ isDirty: false }),
}));
